import * as reduce from 'lodash/reduce'
import * as values from 'lodash/values'
import * as orderBy from 'lodash/orderBy'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'
import { BaseInsight, Insight, InsightsCollection } from 'interfaces/insights'
import { OPPORTUNITY_CATEGORY_IDS, OPPORTUNITY_PREDEFINED_CATEGORY_IDS_SET } from 'constants/opportunities'
import { OpportunityCategoryId, MetaData, OpportunityPredefinedCategoryId } from 'interfaces/meta'
import { isZeroStateInsight } from 'helpers/insightHelpers'
import { OpportunityCategory, OpportunityCategoryGroupKey, OpportunitiesPageTitlesMap, OpportunitiesCategoriesMap } from 'interfaces/opportunities'
import { OPPORTUNITIES_URL } from 'tangle-shared'
import { SocialInnerInsight } from 'interfaces/socialInsights'

export const getAllNewOpportunitiesCount = (insightsCollection: InsightsCollection | null): number => {
  if (!insightsCollection) {
    return 0
  }

  const allOpportunities = getItemsFromCollection<Insight, 'id'>(insightsCollection)
  const newOpportunities = allOpportunities.filter(
    (opportunity: BaseInsight) => opportunity.is_new && !opportunity.is_dismissed
  )

  return newOpportunities.length
}

type InsightCategoryAttributesPredicateResult = {
  isInCategory: boolean
  isTreatedNew: boolean // because `is_new` will be confusing
}
type InsightCategoryAttributesPredicate = (opportunity: BaseInsight) => InsightCategoryAttributesPredicateResult
type CategoryPredicates = Record<OpportunityCategoryId, InsightCategoryAttributesPredicate>

type InsightCategoryAttributes = Record<OpportunityCategoryId, InsightCategoryAttributesPredicateResult>

const createCategoryPredicate = (categoryId: OpportunityCategoryId): InsightCategoryAttributesPredicate =>
  (opportunity) => ({
    isInCategory: !isZeroStateInsight(opportunity) && opportunity.category === categoryId,
    isTreatedNew: opportunity.is_new,
  })

const bindCategoriesToPredicate = (categoryIds: OpportunityCategoryId[]) => categoryIds.reduce<CategoryPredicates>(
  (result, categoryId) => ({
    ...result,
    [categoryId]: createCategoryPredicate(categoryId),
  }),
  {} as CategoryPredicates,
)

const INSIGHT_CATEGORY_ATTRIBUTES_PREDICATES: Record<OpportunityCategoryId, InsightCategoryAttributesPredicate> = {
  ...bindCategoriesToPredicate(OPPORTUNITY_CATEGORY_IDS),
  top: () => ({ isInCategory: true, isTreatedNew: false }),
  new: ({ is_new }) => ({ isInCategory: is_new, isTreatedNew: true }),
  rewardable: ({ reward, is_new }) => ({ isInCategory: Boolean(reward), isTreatedNew: is_new }),
  dismissed: ({ is_dismissed }) => ({ isInCategory: is_dismissed, isTreatedNew: false }),
}

export const getInsightCategoryAttributes = (opportunity: BaseInsight): InsightCategoryAttributes =>
  reduce<InsightCategoryAttributesPredicate, InsightCategoryAttributes>(
    INSIGHT_CATEGORY_ATTRIBUTES_PREDICATES,
    (result, predicate, key) => ({
      ...result,
      [key]: predicate(opportunity),
    }),
    {} as InsightCategoryAttributes,
  )

type CreateEmptyCategoryArg = {
  id: OpportunityCategoryId
  order?: number
  titles: OpportunitiesPageTitlesMap
  meta: MetaData
}
export const createEmptyCategory = (arg: CreateEmptyCategoryArg): OpportunityCategory => {
  const {
    id,
    order = 0,
    titles,
    meta,
  } = arg

  let isPreset = false
  let group: OpportunityCategoryGroupKey | null = null
  if (id in meta.spending_categories.byKey) {
    group = 'spending'
  }
  if (id in meta.income_categories.byKey) {
    group = 'income'
  }
  if (id in meta.debt_categories.byKey) {
    group = 'debts'
  }
  if (id in meta.asset_categories.byKey) {
    group = 'assets'
  }
  if (OPPORTUNITY_PREDEFINED_CATEGORY_IDS_SET.has(id as OpportunityPredefinedCategoryId)) {
    group = 'predefined'
    isPreset = true
  }

  const title = titles[id] || ''

  return {
    id,
    title,
    order,
    newOpportunitiesCount: 0,
    opportunities: [],
    bestMatch: 0,
    isPreset,
    group,
  }
}

export const isOpportunityPage = () => {
  return location.pathname === OPPORTUNITIES_URL
}

export const shapeOpportunityCategories = (titles: OpportunitiesPageTitlesMap, opportunities: SocialInnerInsight[], meta: MetaData): OpportunityCategory[] => {
  const topOpportunitiesCategory = createEmptyCategory({ id: 'top', order: 3, titles, meta })
  const newOpportunitiesCategory = createEmptyCategory({ id: 'new', order: 2, titles, meta })
  const rewardableOpportunitiesCategory = createEmptyCategory({ id: 'rewardable', order: 1, titles, meta })
  const dismissedOpportunitiesCategory = createEmptyCategory({ id: 'dismissed', order: -1, titles, meta })
  const categoriesById: OpportunitiesCategoriesMap = {
    top: topOpportunitiesCategory,
    new: newOpportunitiesCategory,
    rewardable: rewardableOpportunitiesCategory,
    dismissed: dismissedOpportunitiesCategory,
  }

  /*
    We generate category attributes object for every opportunity.
    The object has all the opp categories as keys and `InsightCategoryAttributesPredicateResult` as values.
    Scanning every opportunity we form overall categories with their own opportunity arrays and `newOpportunitiesCount` counters.
  */
  opportunities.forEach((opportunity) => {
    const categoryAttributes = getInsightCategoryAttributes(opportunity)
    let profileCategoryId: OpportunityCategoryId
    for (profileCategoryId in categoryAttributes) {
      const { isInCategory, isTreatedNew } = categoryAttributes[profileCategoryId]

      if (!isInCategory) {
        continue
      }

      if (opportunity.is_dismissed && profileCategoryId !== 'dismissed') {
        continue
      }

      if (!categoriesById[profileCategoryId]) {
        categoriesById[profileCategoryId] = createEmptyCategory({ id: profileCategoryId, titles, meta })
      }

      const currentCategory = categoriesById[profileCategoryId]
      if (currentCategory) {
        if (isTreatedNew) {
          currentCategory.newOpportunitiesCount++
        }

        currentCategory.opportunities.push(opportunity)

        if (opportunity.match && opportunity.match.value) {
          currentCategory.bestMatch = Math.max(currentCategory.bestMatch, opportunity.match.value)
        }
      }
    }
  })

  const categories = orderBy(values<OpportunityCategory>(categoriesById), ['order', 'bestMatch', 'title'], ['desc', 'desc', 'asc'])

  return categories
}

export const isCategoryPredefined = (categoryId: OpportunityCategoryId) =>
  OPPORTUNITY_PREDEFINED_CATEGORY_IDS_SET.has(categoryId as OpportunityPredefinedCategoryId)
